import React, { useState } from 'react';
import { MapPin, Phone, Mail, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '', company: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Something went wrong. Please try again.');
      }

      setStatus('success');
      setFormData({ name: '', email: '', message: '', company: '' });
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <section id="contact" className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sky-400 font-semibold text-sm uppercase tracking-wider mb-3">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Contact Us
          </h2>
          <p className="text-slate-400 text-lg">
            Ready to start your project? Reach out to our team for expert consultation.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="space-y-12">
            <div className="text-center">
              <p className="text-slate-400 leading-relaxed text-lg">
                Whether you're planning a new resort development, need coastal protection solutions,
                or require expert consultation for your construction project, we're here to help.
              </p>
            </div>

            {/* Contact Form */}
            <form onSubmit={handleSubmit} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 sm:p-8 space-y-5">
              {/* Honeypot field — hidden from real visitors, bots tend to fill it */}
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                className="absolute -left-[9999px] w-px h-px opacity-0"
              />

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={status === 'submitting'}
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={status === 'submitting'}
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-300">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={status === 'submitting'}
                  rows={5}
                  className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                  placeholder="Tell us about your project..."
                />
              </div>

              {status === 'success' && (
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  Message sent — we'll be in touch shortly.
                </div>
              )}

              {status === 'error' && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {errorMessage}
                </div>
              )}

              <Button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-sky-500 hover:bg-sky-600 text-white"
              >
                {status === 'submitting' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </Button>
            </form>

            {/* Contact Cards */}
            <div className="grid sm:grid-cols-3 gap-6">
              {/* Address */}
              <div className="flex flex-col items-center text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-sky-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-sky-500/10 flex items-center justify-center mb-4">
                  <MapPin className="w-7 h-7 text-sky-400" />
                </div>
                <h4 className="text-white font-medium mb-2">Office Address</h4>
                <p className="text-slate-400 text-sm">{contactInfo.address}</p>
              </div>

              {/* Phone */}
              <div className="flex flex-col items-center text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-yellow-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-yellow-500/10 flex items-center justify-center mb-4">
                  <Phone className="w-7 h-7 text-yellow-400" />
                </div>
                <h4 className="text-white font-medium mb-2">Phone Numbers</h4>
                <div className="space-y-1">
                  {contactInfo.phones.map((phone, index) => (
                    <a
                      key={index}
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      className="block text-slate-400 text-sm hover:text-yellow-400 transition-colors"
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div className="flex flex-col items-center text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-green-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-green-500/10 flex items-center justify-center mb-4">
                  <Mail className="w-7 h-7 text-green-400" />
                </div>
                <h4 className="text-white font-medium mb-2">Email Addresses</h4>
                <div className="space-y-1">
                  {contactInfo.emails.map((email, index) => (
                    <a
                      key={index}
                      href={`mailto:${email}`}
                      className="block text-slate-400 text-sm hover:text-green-400 transition-colors"
                    >
                      {email}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
